import React, { useMemo } from 'react';
import styled from 'styled-components';

import { formatNumber } from '@column/column-ui-kit';
import { Badge, TextOverflow, cn } from '@column/ui';

import { type ReviewCurrencyTotal, type ReviewDirectionTotals, type ReviewTypeCount } from '../_utils/reviewTotals';
import { TransferAmount, TransferHeaderFrame, TransferTypeBadge } from '~/lib/transfers';
import { getStatusIconClassName } from '~/lib/transfers/status';
import { getCountChipClassName, getTransferBadgeClassName, getTransferIcon } from '~/lib/transfers/utils';
import { type TransferEntryType } from '~/repositories';
import { Card, Flex, Text } from '~/styles';

const StyledCard = styled(Card)`
  .transfer-page-header {
    padding: 16px 16px 12px;
  }
`;

const SplitBalance = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 1px minmax(0, 1fr);
  margin: -16px -16px 4px;
  border-bottom: 1px solid ${({ theme }) => theme.secondary.blendToBackground(200)};
`;

const SplitCell = styled.div`
  padding: 16px 16px 12px;
`;

const BalanceDivider = styled.div`
  align-self: stretch;
  width: 1px;
  background: ${({ theme }) => theme.secondary.blendToBackground(200)};
`;

const DirectionTotals: React.FC<{ label?: string; totals: ReviewCurrencyTotal[]; isAdding: boolean }> = ({
  label,
  totals,
  isAdding,
}) => (
  <Flex flexDirection="column" gap={4}>
    {totals.map(({ currencyCode, amount }) => (
      <TransferAmount
        key={currencyCode}
        className="text-2xl font-semibold leading-6 tracking-[-0.02em]"
        isAdding={isAdding}
        signPlacement="inline"
      >
        {formatNumber(amount, currencyCode)}
        <Text className="ml-1" size={14} color="lighter" weight="medium">
          {currencyCode}
        </Text>
      </TransferAmount>
    ))}
    {label && (
      <Text size={14} color="light" weight="medium" whiteSpace="nowrap">
        {label}
      </Text>
    )}
  </Flex>
);

const renderTotals = (totals: ReviewDirectionTotals | undefined) => {
  if (!totals || (totals.debits.length === 0 && totals.credits.length === 0)) {
    return (
      <Text size={24} weight="semibold" color="lighter" lineHeight="24px">
        -
      </Text>
    );
  }
  if (totals.debits.length > 0 && totals.credits.length > 0) {
    return (
      <SplitBalance>
        <SplitCell>
          <DirectionTotals label="Debits" totals={totals.debits} isAdding={false} />
        </SplitCell>
        <BalanceDivider />
        <SplitCell>
          <DirectionTotals label="Credits" totals={totals.credits} isAdding />
        </SplitCell>
      </SplitBalance>
    );
  }
  return totals.debits.length > 0 ? (
    <DirectionTotals totals={totals.debits} isAdding={false} />
  ) : (
    <DirectionTotals totals={totals.credits} isAdding />
  );
};

interface ReviewBatchHeaderProps {
  totals?: ReviewDirectionTotals;
  typeCounts?: ReviewTypeCount[];
}

const groupByIcon = (typeCounts: ReviewTypeCount[]) => {
  const groups = new Map<unknown, { key: string; icon: React.ReactNode; count: number; items: ReviewTypeCount[] }>();
  typeCounts.forEach((typeCount) => {
    const icon = getTransferIcon(typeCount.type);
    const iconKey = React.isValidElement(icon) ? icon.type : typeCount.label;
    const existing = groups.get(iconKey);
    groups.set(iconKey, {
      key: existing?.key ?? typeCount.label,
      icon,
      count: (existing?.count ?? 0) + typeCount.count,
      items: [...(existing?.items ?? []), typeCount],
    });
  });
  return [...groups.values()];
};

export const ReviewBatchHeader: React.FC<ReviewBatchHeaderProps> = ({ totals, typeCounts }) => {
  const iconGroups = useMemo(() => (typeCounts ? groupByIcon(typeCounts) : []), [typeCounts]);
  return (
    <StyledCard>
      <TransferHeaderFrame idLabel="Transfer ID" className="transfer-page-header">
        <Flex flexDirection="column" gap={8}>
          {renderTotals(totals)}
          {typeCounts && (
            <TextOverflow
              className="self-start"
              delay={0}
              alwaysShow
              hoverContent={
                <span className="flex flex-col items-start gap-1.5">
                  {iconGroups
                    .flatMap(({ items }) => items)
                    .map(({ type, label, count }) => (
                      <span
                        key={label}
                        className="inline-flex items-center gap-1 [&>[data-slot=badge]:first-child]:pr-0"
                      >
                        <TransferTypeBadge type={type as TransferEntryType} />
                        <Badge variant="ghost" className={getCountChipClassName()}>
                          {formatNumber(count, false)}
                        </Badge>
                      </span>
                    ))}
                </span>
              }
            >
              <span
                className="flex items-center gap-1.5"
                role="img"
                aria-label={typeCounts.map(({ label, count }) => `${formatNumber(count, false)} ${label}`).join(', ')}
              >
                {iconGroups.map(({ key, icon, count }) => (
                  <Badge key={key} variant="ghost" className={cn(getTransferBadgeClassName(), 'pr-0')}>
                    <span className={getStatusIconClassName('secondary')}>{icon}</span>
                    {formatNumber(count, false)}
                  </Badge>
                ))}
              </span>
            </TextOverflow>
          )}
        </Flex>
      </TransferHeaderFrame>
    </StyledCard>
  );
};
